<template>
    <div>
        <!-- สรุปยอด -->
        <b-row class="mb-1">
            <b-col md="3">
                <b-card class="text-center mb-1">
                    <h4 class="mb-0">{{ totals.calls || 0 }}</h4>
                    <small class="text-muted">Model calls</small>
                </b-card>
            </b-col>
            <b-col md="3">
                <b-card class="text-center mb-1">
                    <h4 class="mb-0 text-danger">{{ providerCount('anthropic') }}</h4>
                    <small class="text-muted">Anthropic (เสียเงิน)</small>
                </b-card>
            </b-col>
            <b-col md="3">
                <b-card class="text-center mb-1">
                    <h4 class="mb-0 text-success">{{ providerCount('typhoon') }}</h4>
                    <small class="text-muted">Typhoon (ฟรี)</small>
                </b-card>
            </b-col>
            <b-col md="3">
                <b-card class="text-center mb-1">
                    <h4 class="mb-0">{{ providerCount('fast-path') }}</h4>
                    <small class="text-muted">Fast path (ไม่เรียกโมเดล)</small>
                </b-card>
            </b-col>
        </b-row>

        <b-card title="AI Keyword Usage">
            <b-row class="mb-2">
                <b-col md="2">
                    <b-form-group label="ย้อนหลัง" label-for="days-filter">
                        <b-form-select
                            id="days-filter"
                            v-model="days"
                            :options="dayOptions"
                            :disabled="isLoading"
                            @change="onFilterChange"
                        />
                    </b-form-group>
                </b-col>
                <b-col md="2">
                    <b-form-group label="Provider" label-for="provider-filter">
                        <b-form-select
                            id="provider-filter"
                            v-model="provider"
                            :options="providerOptions"
                            :disabled="isLoading"
                            @change="onFilterChange"
                        />
                    </b-form-group>
                </b-col>
                <b-col md="4">
                    <b-form-group label="ค้นในคำค้น" label-for="kw-filter">
                        <b-form-input
                            id="kw-filter"
                            v-model="keyword"
                            placeholder="เช่น อโศก"
                            :disabled="isLoading"
                            @keyup.enter="onFilterChange"
                        />
                    </b-form-group>
                </b-col>
                <b-col md="2">
                    <b-form-group label="เรียงตาม" label-for="sort-filter">
                        <b-form-select
                            id="sort-filter"
                            v-model="sortBy"
                            :options="sortOptions"
                            :disabled="isLoading"
                            @change="onFilterChange"
                        />
                    </b-form-group>
                </b-col>
                <b-col md="2">
                    <b-form-group label="ลำดับ" label-for="dir-filter">
                        <b-form-select
                            id="dir-filter"
                            v-model="sortDir"
                            :options="dirOptions"
                            :disabled="isLoading"
                            @change="onFilterChange"
                        />
                    </b-form-group>
                </b-col>
                <b-col md="4" class="d-flex align-items-end mb-1">
                    <b-button variant="primary" :disabled="isLoading" @click="onFilterChange">
                        ค้นหา
                    </b-button>
                    <b-button variant="outline-secondary" class="ml-1" :disabled="isLoading" @click="resetFilter">
                        ล้าง
                    </b-button>
                </b-col>
            </b-row>

            <b-tabs v-model="tabIndex" @input="onTabChange">
                <!-- สรุปรายคำค้น -->
                <b-tab title="สรุปตามคำค้น">
                    <!--
                        no-local-sorting: ให้ b-table แค่ส่ง event ไม่ต้องเรียงเอง
                        เพราะข้อมูลที่โหลดมาเป็นแค่ top-N ถ้าเรียงในเครื่องจะได้
                        "ล่าสุด" เฉพาะในบรรดาคำที่เรียกบ่อยสุด ไม่ใช่ล่าสุดจริง
                    -->
                    <b-table
                        :items="keywords"
                        :fields="keywordFields"
                        :busy="isLoading"
                        :sort-by="tableSortBy"
                        :sort-desc="sortDir === 'desc'"
                        no-local-sorting
                        responsive
                        show-empty
                        empty-text="ไม่มีข้อมูล"
                        class="mt-1"
                        @sort-changed="onSortChanged"
                    >
                        <template #cell(keyword)="data">
                            <span class="font-weight-bold">{{ data.item.keyword }}</span>
                        </template>
                        <template #cell(providers)="data">
                            <b-badge
                                v-for="(count, name) in data.item.providers"
                                :key="name"
                                :variant="providerVariant(name)"
                                class="mr-25"
                            >
                                {{ name }} {{ count }}
                            </b-badge>
                        </template>
                        <template #cell(tokens)="data">
                            <small>
                                in {{ data.item.input_tokens }} · cached {{ data.item.cached_tokens }} ·
                                out {{ data.item.output_tokens }}
                            </small>
                        </template>
                        <template #cell(last_at)="data">
                            <small>{{ formatDate(data.item.last_at) }}</small>
                        </template>
                    </b-table>
                </b-tab>

                <!-- รายการดิบ -->
                <b-tab title="รายการล่าสุด">
                    <b-table
                        :items="logs"
                        :fields="logFields"
                        :busy="isLoading"
                        responsive
                        show-empty
                        empty-text="ไม่มีข้อมูล"
                        class="mt-1"
                    >
                        <template #cell(provider)="data">
                            <b-badge :variant="providerVariant(data.item.provider)">
                                {{ data.item.provider }}
                            </b-badge>
                            <b-badge v-if="data.item.fell_back_from" variant="warning" class="ml-25">
                                สำรองจาก {{ data.item.fell_back_from }}
                            </b-badge>
                        </template>
                        <template #cell(ok)="data">
                            <b-badge :variant="data.item.ok ? 'light-success' : 'light-danger'">
                                {{ data.item.ok ? 'ok' : 'error' }}
                            </b-badge>
                        </template>
                        <template #cell(at)="data">
                            <small>{{ formatDate(data.item.at) }}</small>
                        </template>
                        <template #cell(tokens)="data">
                            <small>{{ data.item.input_tokens }} / {{ data.item.cached_tokens }} / {{ data.item.output_tokens }}</small>
                        </template>
                    </b-table>
                </b-tab>
            </b-tabs>
        </b-card>
    </div>
</template>

<script>
/*
 * รายงานว่าคำค้นไหนทำให้ระบบต้องเรียกโมเดล AI
 *
 * อ่านจาก logs/anthropic-usage.log ผ่าน GET /ai-usage/v1/keywords และ /ai-usage/v1/logs
 * (อ่านอย่างเดียว ไม่มีการเขียน/ลบ log จากหน้านี้)
 *
 * ใช้ตัดสินใจว่าควรเติมข้อมูลอะไรในฐาน — คำที่ขึ้นบนสุดคือคำที่เรียกโมเดลบ่อยที่สุด
 * ถ้าทำให้คำนั้นเข้า fast path ได้ (เช่นเติมชื่อไทยของโซน) จะประหยัดได้มากที่สุด
 */
import {
    BCard, BTable, BBadge, BFormSelect, BFormGroup, BFormInput,
    BButton, BRow, BCol, BTabs, BTab,
} from 'bootstrap-vue'
import axios from '@axios'

export default {
    components: {
        BCard, BTable, BBadge, BFormSelect, BFormGroup, BFormInput,
        BButton, BRow, BCol, BTabs, BTab,
    },
    data() {
        return {
            keywords: [],
            logs: [],
            totals: {},
            isLoading: false,
            tabIndex: 0,
            days: 7,
            provider: '',
            keyword: '',
            sortBy: 'calls',
            sortDir: 'desc',
            sortOptions: [
                { value: 'calls', text: 'จำนวนครั้งที่เรียก' },
                { value: 'last', text: 'พิมพ์ล่าสุด' },
                { value: 'keyword', text: 'ตัวอักษร (ก-ฮ / A-Z)' },
                { value: 'tokens', text: 'Token ที่ใช้' },
            ],
            dirOptions: [
                { value: 'desc', text: 'มาก → น้อย' },
                { value: 'asc', text: 'น้อย → มาก' },
            ],
            dayOptions: [
                { value: 1, text: '24 ชั่วโมง' },
                { value: 7, text: '7 วัน' },
                { value: 30, text: '30 วัน' },
                { value: 0, text: 'ทั้งหมด' },
            ],
            providerOptions: [
                { value: '', text: 'ทุกเจ้า' },
                { value: 'anthropic', text: 'Anthropic (เสียเงิน)' },
                { value: 'typhoon', text: 'Typhoon (ฟรี)' },
                { value: 'fast-path', text: 'Fast path' },
            ],
            // คลิกหัวคอลัมน์เพื่อเรียง (ยิงกลับไปเรียงที่ server ไม่ใช่เรียงเฉพาะหน้าปัจจุบัน)
            keywordFields: [
                { key: 'keyword', label: 'คำค้น', sortable: true },
                { key: 'calls', label: 'เรียก', class: 'text-center', sortable: true },
                { key: 'providers', label: 'เจ้าที่ใช้' },
                { key: 'tokens', label: 'Token', sortable: true },
                { key: 'last_at', label: 'ล่าสุด', sortable: true },
            ],
            // ชื่อคอลัมน์ในตาราง -> ชื่อ sort ที่ API รู้จัก
            columnToSort: { keyword: 'keyword', calls: 'calls', tokens: 'tokens', last_at: 'last' },
            logFields: [
                { key: 'at', label: 'เวลา' },
                { key: 'keyword', label: 'คำค้น' },
                { key: 'provider', label: 'Provider' },
                { key: 'service', label: 'Service' },
                { key: 'model', label: 'Model' },
                { key: 'tokens', label: 'in/cached/out' },
                { key: 'ms', label: 'ms', class: 'text-right' },
                { key: 'ok', label: '' },
            ],
        }
    },
    computed: {
        // b-table ต้องการชื่อ "คอลัมน์" ส่วน state เราเก็บเป็นชื่อ sort ของ API
        tableSortBy() {
            const found = Object.entries(this.columnToSort).find(([, v]) => v === this.sortBy)

            return found ? found[0] : 'calls'
        },
    },
    created() {
        this.fetchData()
    },
    methods: {
        query() {
            const params = { limit: 200 }

            if (this.days) params.days = this.days
            if (this.provider) params.provider = this.provider
            if (this.keyword) params.keyword = this.keyword

            // เรียงที่ server เสมอ ไม่งั้น "ล่าสุด" จะได้แค่ล่าสุดในบรรดา top-N ที่เรียกบ่อยสุด
            params.sort = this.sortBy
            params.dir = this.sortDir

            return { params }
        },
        async fetchData() {
            try {
                this.isLoading = true

                // ดึงทั้งสองแท็บพร้อมกัน ตัวเลขสรุปด้านบนมาจาก /logs
                const [kw, lg] = await Promise.all([
                    axios.get('ai-usage/v1/keywords', this.query()),
                    axios.get('ai-usage/v1/logs', this.query()),
                ])

                this.keywords = kw?.data?.data || []
                this.logs = lg?.data?.data || []
                this.totals = lg?.data?.totals || {}
            } catch (error) {
                console.error('Error loading AI usage logs:', error)
                this.keywords = []
                this.logs = []
                this.totals = {}
            } finally {
                this.isLoading = false
            }
        },
        onFilterChange() {
            this.fetchData()
        },
        onSortChanged(ctx) {
            const mapped = this.columnToSort[ctx.sortBy]

            if (!mapped) return

            this.sortBy = mapped
            this.sortDir = ctx.sortDesc ? 'desc' : 'asc'
            this.fetchData()
        },
        onTabChange() {
            // ทั้งสองแท็บโหลดมาพร้อมกันแล้ว ไม่ต้องยิงซ้ำตอนสลับ
        },
        resetFilter() {
            this.days = 7
            this.provider = ''
            this.keyword = ''
            this.fetchData()
        },
        providerCount(name) {
            return (this.totals.byProvider || {})[name] || 0
        },
        providerVariant(name) {
            if (name === 'anthropic') return 'light-danger'
            if (name === 'typhoon') return 'light-success'
            if (name === 'fast-path') return 'light-secondary'

            return 'light-info'
        },
        formatDate(value) {
            if (!value) return '-'

            const d = new Date(value)

            return Number.isNaN(d.getTime()) ? value : d.toLocaleString('th-TH')
        },
    },
}
</script>
